// Duplicate detection for the "Invite Teacher" form on the teachers list.
//
// Ops was creating a second profile for a teacher who already existed, because
// the invite form gave no signal that the name was already on the roster. The
// full roster is already in Redux by the time the form is usable, so the check
// runs locally against that list rather than adding an API round trip.

import { teacherFullNameOf } from '../../../../utils/teacher_name';

// Canonical form of a name: case, accents, punctuation and repeated whitespace
// removed, so "Anjali  Rao", "anjali rao" and "Anjali Rào" all collapse to the
// same key. Diacritics are stripped before punctuation is folded to spaces —
// doing it the other way round would turn the decomposed "à" into "a o" rather
// than "a".
export const normalize_name = (name) =>
  (name || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

// A teacher is matchable on either "first last" or "first middle last" so an
// existing profile is still found when the middle name is recorded on only one
// side of the comparison. last_name is only an initial, so the real surname
// (last_name_full_value) is matched too: "Anjali Rao" finds an existing "Anjali R".
export const teacher_name_keys = (teacher = {}) => {
  const first = teacher.first_name || '';
  const middle = teacher.middle_name || '';
  const surnames = [teacher.last_name || ''];
  if (teacher.last_name_full_value && teacher.last_name_full_value !== teacher.last_name) {
    surnames.push(teacher.last_name_full_value);
  }
  return surnames.flatMap((last) => [
    normalize_name(`${first} ${last}`),
    normalize_name(`${first} ${middle} ${last}`)
  ]);
};

// Pre-computes the normalized keys for the whole roster once so that matching on
// each keystroke is a cheap string comparison rather than a re-normalization of
// every teacher.
export const build_name_index = (teachers = []) =>
  teachers.map((teacher) => ({ teacher, keys: teacher_name_keys(teacher) }));

// Exact match on a canonical full name. Deliberately not a substring or
// first-name match: on a roster this size that would fire on nearly every common
// first name and train ops to click through the warning.
export const find_duplicates_in_index = (name_index, name) => {
  const entered = normalize_name(name);
  if (!entered) {
    return [];
  }
  return (name_index || [])
    .filter((entry) => entry.keys.includes(entered))
    .map((entry) => entry.teacher);
};

export const full_name = (teacher = {}) => teacherFullNameOf(teacher);
